import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import Profile from './components/Profile';
import AdminDashboard from './pages/AdminDashboard';
import RecruiterDashboard from './pages/RecruiterDashboard';
import FinancialDashboard from './pages/FinancialDashboard';
import HRDashboard from './pages/HRDashboard';
import CommsMarketingDashboard from './pages/CommsMarketingDashboard';
import RecruiterApplication from './pages/RecruiterApplication';
import PublicRecruiterVerify from './pages/PublicRecruiterVerify';
import Navbar from './components/Navbar';
import Icon from './components/Icon';
import { settingsAPI } from './services/api';
import AIAssistant from './components/AIAssistant';
import { applyTheme, initTheme } from './utils/theme';
import { getDashboardPath, canAccessDashboard } from './utils/roleUtils';
import TopBar from './components/TopBar';
import CommandPalette from './components/CommandPalette';

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

function AppShell() {
  const location = useLocation();
  const isPublicVerify = location.pathname === '/verify';

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  useEffect(() => {
    initTheme();
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (token && userData) {
      setUser(JSON.parse(userData));
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    settingsAPI.getPublic()
      .then((res) => {
        const data = res.data || {};
        applyTheme(data);
        initTheme();
        setMaintenanceMode(!!data.general?.maintenanceMode);
      })
      .catch(() => {});
  }, []);

  const handleLogin = (token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh',
        background: 'var(--background-color)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div className="stat-card-icon" style={{ width: '64px', height: '64px', fontSize: '24px', margin: '0 auto 16px' }}>
            OUK
          </div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Routes>
        <Route path="/verify" element={<PublicRecruiterVerify />} />
      </Routes>

      {!isPublicVerify && (
        <div className="app-layout">
          {user && <Navbar user={user} onLogout={handleLogout} />}
          
          <div className="app-main">
          <ToastContainer 
            position="top-right" 
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />

          {maintenanceMode && user && user.role !== 'admin' && (
            <div className="maintenance-banner">
              <Icon name="info" /> The platform is currently in maintenance mode. Some features may be temporarily unavailable.
            </div>
          )}

          {user && <TopBar user={user} onLogout={handleLogout} onOpenSearch={() => setPaletteOpen(true)} />}



          <Routes>
            <Route path="/login" element={
              user ? <Navigate to={getDashboardPath(user.permissions, user.role)} /> : 
              <Login onLogin={handleLogin} />
            } />
            
            <Route path="/forgot-password" element={
              user ? <Navigate to={getDashboardPath(user.permissions, user.role)} /> : 
              <ForgotPassword />
            } />
            
            <Route path="/reset-password" element={
              user ? <Navigate to={getDashboardPath(user.permissions, user.role)} /> : 
              <ResetPassword />
            } />
            
            <Route path="/apply" element={<RecruiterApplication />} />
            
            <Route path="/change-password" element={
              user ? <ChangePassword user={user} /> : <Navigate to="/login" />
            } />

            <Route path="/profile" element={
              user ? <Profile /> : <Navigate to="/login" />
            } />
            
            <Route path="/admin/*" element={
              user && canAccessDashboard(user.permissions, 'admin', user.role) ? <AdminDashboard /> : <Navigate to="/login" />
            } />
            
            <Route path="/recruiter/*" element={
              user && canAccessDashboard(user.permissions, 'recruiter', user.role) ? <RecruiterDashboard /> : <Navigate to="/login" />
            } />
            
            <Route path="/financial/*" element={
              user && canAccessDashboard(user.permissions, 'financial', user.role) ? <FinancialDashboard /> : <Navigate to="/login" />
            } />
            
            <Route path="/hr/*" element={
              user && canAccessDashboard(user.permissions, 'hr', user.role) ? <HRDashboard /> : <Navigate to="/login" />
            } />
            
            <Route path="/comms/*" element={
              user && canAccessDashboard(user.permissions, 'comms', user.role) ? <CommsMarketingDashboard /> : <Navigate to="/login" />
            } />
            
            <Route path="/" element={
              <Navigate to={user ? getDashboardPath(user.permissions, user.role) : '/login'} />
            } />
          </Routes>
          <AIAssistant />
          {user && <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} user={user} />}
          </div>
        </div>
      )}
    </>
  );
}

export default App;
